import {
  calculateRectangleProperties,
  getRoomSides,
  getStartAndEndPoints,
} from "./roomPropertiesHelpers";
import { getIntersection } from "./calculations";
import store from "@/store/store";

const calculateAxisPointDistance = (axisLength) => {
  return 0.2 * Math.pow(5, Math.log10(axisLength));
};

const getPointDistances = (roomLength, roomWidth, isWidthXAxis) => {
  const lengthPointDistance = isWidthXAxis
    ? store.state.roomStore.distance_between_points_y
    : store.state.roomStore.distance_between_points_x;
  const widthPointDistance = isWidthXAxis
    ? store.state.roomStore.distance_between_points_x
    : store.state.roomStore.distance_between_points_y;
  return {
    lengthPointDistance,
    widthPointDistance,
    pointsInLength: Math.floor((roomLength - 1) / lengthPointDistance) + 1,
    pointsInWidth: Math.floor((roomWidth - 1) / widthPointDistance) + 1,
  };
};

const calculateAxisLines = (
  pointDistanceInMeters,
  nrOfLines,
  start,
  xAxis,
  oneMeter,
  roomCorners
) => {
  const configs = [];

  const relevantIndexes = xAxis ? [0, 2] : [1, 3];

  for (let i = 0; i < nrOfLines + 4; i++) {
    configs.push({
      points: start.map((point, index) => {
        if (!relevantIndexes.includes(index)) {
          return point;
        }
        return point + pointDistanceInMeters * oneMeter * (i - 2);
      }),
      stroke: "white",
      strokeWidth: 1,
      opacity: 1,
    });
  }

  const relevantConfigs = getRelevantPartOfLines(configs, roomCorners);

  return relevantConfigs.filter((config) =>
    config.points.every((point) => !!point)
  );
};

const getRelevantPartOfLines = (configs, roomCorners) => {
  const relevantConfigs = [];
  configs.forEach((config) => {
    const modifiedLines = getModifiedLines(config.points, roomCorners);
    modifiedLines.forEach((line) => {
      relevantConfigs.push({
        ...config,
        points: line,
      });
    });
  });
  return relevantConfigs;
};

const horizontalLineSort = (a, b) => a.x - b.x;
const verticalLineSort = (a, b) => a.y - b.y;

const getModifiedLines = (linePoints, roomCorners) => {
  const roomSides = getRoomSides(roomCorners);

  const intersections = [];

  const lineStart = { x: linePoints[0], y: linePoints[1] };
  const lineEnd = { x: linePoints[2], y: linePoints[3] };

  roomSides.forEach((side) => {
    const intersection = getIntersection(...side, ...linePoints);
    if (intersection) {
      intersections.push(intersection);
    }
  });

  intersections.sort(
    lineStart.y === lineEnd.y ? horizontalLineSort : verticalLineSort
  );

  const modifiedLines = [];

  for (let i = 0; i < intersections.length - 1; i = i + 2) {
    modifiedLines.push([
      intersections[i].x,
      intersections[i].y,
      intersections[i + 1].x,
      intersections[i + 1].y,
    ]);
  }

  return modifiedLines;
};

export const calculateLineConfigs = (roomCorners, roomLength) => {
  if (!roomLength) {
    return [];
  }

  const {
    height: yInPixels,
    width: xInPixels,
    xMin,
    yMin,
    xMax,
    yMax,
  } = calculateRectangleProperties(roomCorners);

  const realLengthInPixels = Math.max(yInPixels, xInPixels);

  const oneMeter = realLengthInPixels / roomLength;

  const roomWidth = Math.min(yInPixels, xInPixels) / oneMeter;

  const isWidthXAxis = xInPixels < yInPixels;

  const pointDistance = calculateAxisPointDistance(
    roomWidth * 2 < roomLength ? roomWidth : roomLength
  );
  if (!store.state.roomStore.freezeDistanceBetweenPoints) {
    store.commit("roomStore/setDistanceBetweenPointsX", pointDistance);
    store.commit("roomStore/setDistanceBetweenPointsY", pointDistance);
    store.commit(
      "roomStore/setDistanceFromWall",
      calculatedistanceFromWall(roomWidth, roomLength)
    );
  }
  store.commit(
    "roomStore/setMaxDistanceBetweenPoints",
    Math.ceil(pointDistance * 100) / 100
  );

  const {
    widthPointDistance,
    lengthPointDistance,
    pointsInLength,
    pointsInWidth,
  } = getPointDistances(roomLength, roomWidth, isWidthXAxis);

  const roomLeftOnWidth =
    roomWidth - 1 - (pointsInWidth - 1) * widthPointDistance;
  const roomLeftOnLength =
    roomLength - 1 - (pointsInLength - 1) * lengthPointDistance;

  const roomLeftOnYAxis = isWidthXAxis ? roomLeftOnLength : roomLeftOnWidth;
  const roomLeftOnXAxis = isWidthXAxis ? roomLeftOnWidth : roomLeftOnLength;

  const { horizontalStart, verticalStart } = getStartAndEndPoints(
    xMin,
    xMax,
    yMin,
    yMax,
    oneMeter,
    roomLeftOnXAxis,
    roomLeftOnYAxis
  );

  const horizontalLineConfigs = calculateAxisLines(
    isWidthXAxis ? lengthPointDistance : widthPointDistance,
    isWidthXAxis ? pointsInLength : pointsInWidth,
    horizontalStart,
    false,
    oneMeter,
    roomCorners
  );

  const verticalLineConfigs = calculateAxisLines(
    isWidthXAxis ? widthPointDistance : lengthPointDistance,
    isWidthXAxis ? pointsInWidth : pointsInLength,
    verticalStart,
    true,
    oneMeter,
    roomCorners
  );

  return {
    horizontalLineConfigs,
    verticalLineConfigs,
  };
};

const calculatedistanceFromWall = (roomWidth, roomLength) => {
  const smallestDimension = roomWidth < roomLength ? roomWidth : roomLength;
  const distanceFromWall = smallestDimension * 0.15;
  return distanceFromWall < 0.5 ? distanceFromWall : 0.5;
};
